import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "@fg/icons";
import { connectParityApi, type ChannelNotifyPrefs } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

const DEFAULTS: ChannelNotifyPrefs = { posts: false, replies: true, mentions: true, banner: true };

/** Teams' "Channel notifications": what this channel adds to Activity, and whether it also pops a banner. */
export function ChannelNotifyDialog({ channelId, channelName, onClose }: { channelId: string; channelName: string; onClose: () => void }) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const key = channelId.replace(/-/g, "").toLowerCase();
    const all = useQuery({
        queryKey: ["channel-notify"],
        queryFn: () => connectParityApi.getChannelNotify(token),
        enabled: Boolean(token)
    });
    const [prefs, setPrefs] = useState<ChannelNotifyPrefs>(DEFAULTS);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { if (all.data) setPrefs({ ...DEFAULTS, ...(all.data[key] ?? {}) }); }, [all.data, key]);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await connectParityApi.setChannelNotify(channelId, prefs, token);
            await qc.invalidateQueries({ queryKey: ["channel-notify"] });
            onClose();
        } catch {
            setError("Couldn't save. Try again.");
        } finally {
            setSaving(false);
        }
    };

    const rows: Array<[keyof ChannelNotifyPrefs, string, string]> = [
        ["posts", "All new posts", "Every new post in #" + channelName + " shows in Activity."],
        ["replies", "Replies to threads you're in", "When someone replies to your post or a thread you replied in."],
        ["mentions", "Channel mentions", "When someone uses @channel or @everyone here."]
    ];

    return (
        <div role="dialog" aria-modal="true" aria-label={`Notifications for ${channelName}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="w-full max-w-md overflow-hidden rounded-lg border bg-card shadow-2xl">
                <header className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Channel notifications: #{channelName}</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
                </header>
                <div className="space-y-1 p-4">
                    {all.isPending ? <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" />Loading…</p> : (
                        <>
                            {rows.map(([k, label, hint]) => (
                                <label key={k} className="flex cursor-pointer items-center justify-between gap-4 rounded-md px-1 py-2 hover:bg-accent/40">
                                    <span><span className="block text-sm font-medium">{label}</span><span className="block text-xs text-muted-foreground">{hint}</span></span>
                                    <input type="checkbox" role="switch" checked={Boolean(prefs[k])} onChange={(e) => setPrefs((p) => ({ ...p, [k]: e.target.checked }))} className="h-4 w-4 shrink-0" />
                                </label>
                            ))}
                            <div role="radiogroup" aria-label="Where notifications show" className="pt-2">
                                <p className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Show as</p>
                                {([[true, "Activity and banner"], [false, "Activity only"]] as const).map(([v, label]) => (
                                    <button
                                        key={label}
                                        type="button"
                                        role="radio"
                                        aria-checked={prefs.banner === v}
                                        onClick={() => setPrefs((p) => ({ ...p, banner: v }))}
                                        className={cn("mr-2 rounded-md border px-3 py-1.5 text-xs", prefs.banner === v ? "border-primary bg-primary/10 font-medium" : "hover:bg-accent")}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            {error ? <p role="alert" className="pt-1 text-xs text-destructive">{error}</p> : null}
                        </>
                    )}
                </div>
                <footer className="flex justify-between border-t px-4 py-3">
                    <button type="button" onClick={() => setPrefs(DEFAULTS)} className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent">Reset to defaults</button>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent">Cancel</button>
                        <button type="button" disabled={saving || all.isPending} onClick={() => void save()} className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
